import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Library, Search, CheckSquare, Square, X, Check, BookOpen, EyeOff, RotateCcw } from 'lucide-react';
import { fetchWords, fetchSheets } from '../api/words';
import { fetchUserStates, setWordStatus, bulkSetStatus } from '../api/progress';
import type { Word, UserWordState, StudyStatus } from '../types';

const PAGE_SIZE = 100;

const STATUS_META: Record<string, { label: string; color: string; bg: string }> = {
  unseen: { label: 'Unseen', color: 'text-text-dim', bg: 'bg-white/[0.04]' },
  new: { label: 'New', color: 'text-text', bg: 'bg-white/[0.06]' },
  learning: { label: 'Learning', color: 'text-amber', bg: 'bg-amber-muted' },
  review: { label: 'Review', color: 'text-blue', bg: 'bg-blue-muted' },
  relearning: { label: 'Relearning', color: 'text-amber', bg: 'bg-amber-muted' },
  mastered: { label: 'Mastered', color: 'text-green', bg: 'bg-green-muted' },
  suspended: { label: 'Hidden', color: 'text-text-dim', bg: 'bg-white/[0.04]' },
};

const FILTERS = ['all', 'unseen', 'learning', 'review', 'mastered', 'suspended'] as const;
type Filter = (typeof FILTERS)[number];

export const WordBrowserPage: React.FC = () => {
  const [words, setWords] = useState<Word[]>([]);
  const [states, setStates] = useState<Map<string, UserWordState>>(new Map());
  const [sheets, setSheets] = useState<{ sheet: string; count: number }[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sheetFilter, setSheetFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<Filter>('all');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');

  useEffect(() => {
    Promise.all([fetchWords(), fetchUserStates(), fetchSheets()])
      .then(([w, s, sh]) => {
        setWords(w);
        setStates(new Map(s.map((st) => [st.word_id.toLowerCase(), st])));
        setSheets(sh);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const statusOf = (word: Word): string => {
    const st = states.get(word.id.toLowerCase());
    return st ? st.status : 'unseen';
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return words.filter((w) => {
      if (sheetFilter && w.sheet !== sheetFilter) return false;
      if (statusFilter !== 'all') {
        const s = statusOf(w);
        if (statusFilter === 'learning') {
          if (!['learning', 'relearning', 'new'].includes(s)) return false;
        } else if (s !== statusFilter) return false;
      }
      if (q && !w.word.toLowerCase().includes(q) && !w.definition.toLowerCase().includes(q)) return false;
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [words, states, search, sheetFilter, statusFilter]);

  const visible = filtered.slice(0, visibleCount);
  const allVisibleSelected = visible.length > 0 && visible.every((w) => selected.has(w.id));

  const counts = useMemo(() => {
    const c = { mastered: 0, learning: 0, suspended: 0, unseen: 0 };
    for (const w of words) {
      const s = statusOf(w);
      if (s === 'mastered') c.mastered++;
      else if (s === 'suspended') c.suspended++;
      else if (s === 'unseen') c.unseen++;
      else c.learning++;
    }
    return c;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [words, states]);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  };

  const applyLocalStatus = (ids: string[], status: StudyStatus) => {
    setStates((prev) => {
      const next = new Map(prev);
      for (const id of ids) {
        const key = id.toLowerCase();
        const existing = next.get(key);
        next.set(key, {
          ...(existing || {
            word_id: key,
            next_review: '',
            interval_days: 0,
            ease_factor: 2.5,
            lapse_count: 0,
            accuracy_history: [],
            response_time_ms: [],
            total_reviews: 0,
            total_correct: 0,
            current_streak: 0,
            best_streak: 0,
            first_seen: '',
            last_reviewed: '',
          }),
          status,
        } as UserWordState);
      }
      return next;
    });
  };

  const handleSingleStatus = async (word: Word, status: StudyStatus) => {
    applyLocalStatus([word.id], status);
    try {
      await setWordStatus(word.id, status);
    } catch {
      showToast('Failed to save — check your connection');
    }
  };

  const handleBulk = async (status: StudyStatus) => {
    if (selected.size === 0 || busy) return;
    setBusy(true);
    const ids = [...selected];
    applyLocalStatus(ids, status);
    try {
      const updated = await bulkSetStatus(ids, status);
      showToast(`${updated} words marked as ${STATUS_META[status]?.label.toLowerCase() || status}`);
      setSelected(new Set());
    } catch {
      showToast('Bulk update failed — check your connection');
    } finally {
      setBusy(false);
    }
  };

  const toggleSelectAll = () => {
    setSelected((prev) => {
      if (allVisibleSelected) {
        const next = new Set(prev);
        visible.forEach((w) => next.delete(w.id));
        return next;
      }
      return new Set([...prev, ...visible.map((w) => w.id)]);
    });
  };

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="flex items-center gap-3 text-text-dim">
          <div className="w-4 h-4 border-2 border-white/10 border-t-gold rounded-full animate-spin" />
          Loading words...
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 pb-32 md:pb-10">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-gold-glow border border-gold-dim flex items-center justify-center">
          <Library size={20} className="text-gold" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-cream">Word Browser</h1>
          <p className="text-xs text-text-dim">
            {words.length} words · {counts.mastered} mastered · {counts.learning} in progress · {counts.unseen} unseen
            {counts.suspended > 0 && ` · ${counts.suspended} hidden`}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-2.5 mb-4">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-dim" />
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setVisibleCount(PAGE_SIZE); }}
            placeholder="Search words or definitions..."
            className="w-full bg-white/[0.04] border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-cream outline-none focus:border-gold placeholder:text-text-dim"
          />
        </div>
        <select
          value={sheetFilter}
          onChange={(e) => { setSheetFilter(e.target.value); setVisibleCount(PAGE_SIZE); }}
          className="bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm text-cream outline-none focus:border-gold"
        >
          <option value="">All Sheets</option>
          {sheets.map((s) => (
            <option key={s.sheet} value={s.sheet}>
              {s.sheet.startsWith('Practice') || s.sheet.startsWith('Pairs') ? s.sheet : `Sheet ${s.sheet}`} ({s.count})
            </option>
          ))}
        </select>
      </div>

      <div className="flex gap-1.5 flex-wrap mb-4">
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => { setStatusFilter(f); setVisibleCount(PAGE_SIZE); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all capitalize ${
              statusFilter === f
                ? 'border-gold-dim bg-gold-glow text-gold'
                : 'border-border bg-surface text-text-dim hover:text-text-hi hover:bg-surface-1'
            }`}
          >
            {f === 'suspended' ? 'Hidden' : f}
          </button>
        ))}
      </div>

      {/* Select-all row */}
      <div className="flex items-center justify-between mb-2 px-1">
        <button
          onClick={toggleSelectAll}
          className="flex items-center gap-2 text-xs text-text-dim hover:text-text-hi transition-colors"
        >
          {allVisibleSelected ? <CheckSquare size={15} className="text-gold" /> : <Square size={15} />}
          {allVisibleSelected ? 'Deselect all' : `Select all shown (${visible.length})`}
        </button>
        <span className="text-[11px] text-text-dim">
          {filtered.length} match{filtered.length === 1 ? '' : 'es'}
        </span>
      </div>

      {/* Word list */}
      <div className="space-y-1.5">
        {visible.map((w) => {
          const s = statusOf(w);
          const meta = STATUS_META[s] || STATUS_META.unseen;
          const isSelected = selected.has(w.id);
          return (
            <div
              key={w.id}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-colors ${
                isSelected ? 'border-gold-dim bg-gold-glow/40' : 'border-border bg-surface hover:bg-surface-1'
              }`}
            >
              <button onClick={() => toggleSelect(w.id)} className="shrink-0 text-text-dim hover:text-gold transition-colors">
                {isSelected ? <CheckSquare size={16} className="text-gold" /> : <Square size={16} />}
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-medium ${s === 'suspended' ? 'text-text-dim line-through' : 'text-text-hi'}`}>
                    {w.word}
                  </span>
                  <span className="text-[10px] text-text-dim">{w.part_of_speech}</span>
                  <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-wider ${meta.color} ${meta.bg}`}>
                    {meta.label}
                  </span>
                </div>
                <p className="text-xs text-text-dim truncate">{w.definition}</p>
              </div>
              <select
                value={['unseen'].includes(s) ? '' : s}
                onChange={(e) => e.target.value && handleSingleStatus(w, e.target.value as StudyStatus)}
                className="shrink-0 bg-white/[0.04] border border-white/10 rounded-lg px-2 py-1.5 text-[11px] text-text outline-none focus:border-gold cursor-pointer"
              >
                <option value="" disabled>Set status…</option>
                <option value="new">New (reset)</option>
                <option value="learning">Learning</option>
                <option value="mastered">Mastered</option>
                <option value="suspended">Hide forever</option>
              </select>
            </div>
          );
        })}
      </div>

      {visibleCount < filtered.length && (
        <button
          onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
          className="w-full mt-4 py-2.5 rounded-xl border border-border bg-surface text-text-dim hover:text-text-hi hover:bg-surface-1 text-sm font-medium transition-all"
        >
          Show more ({filtered.length - visibleCount} remaining)
        </button>
      )}

      {filtered.length === 0 && (
        <div className="text-center py-14 text-sm text-text-dim">No words match your filters.</div>
      )}

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="fixed bottom-20 md:bottom-6 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 px-4 py-3 rounded-2xl glass-strong border border-gold-dim"
          style={{ boxShadow: '0 16px 40px rgba(0,0,0,0.6)' }}
        >
          <span className="text-xs text-text-hi font-medium mr-1">{selected.size} selected</span>
          <button
            onClick={() => handleBulk('mastered')}
            disabled={busy}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-muted border border-green/30 text-green text-xs font-medium hover:brightness-125 transition-all disabled:opacity-50"
          >
            <Check size={13} /> Mastered
          </button>
          <button
            onClick={() => handleBulk('learning')}
            disabled={busy}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-muted border border-amber/30 text-amber text-xs font-medium hover:brightness-125 transition-all disabled:opacity-50"
          >
            <BookOpen size={13} /> Learning
          </button>
          <button
            onClick={() => handleBulk('suspended')}
            disabled={busy}
            className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/[0.06] border border-white/10 text-text-dim text-xs font-medium hover:text-text-hi transition-all disabled:opacity-50"
          >
            <EyeOff size={13} /> Hide
          </button>
          <button
            onClick={() => handleBulk('new')}
            disabled={busy}
            className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/[0.06] border border-white/10 text-text-dim text-xs font-medium hover:text-text-hi transition-all disabled:opacity-50"
          >
            <RotateCcw size={13} /> Reset
          </button>
          <button onClick={() => setSelected(new Set())} className="ml-1 text-text-dim hover:text-text-hi">
            <X size={15} />
          </button>
        </motion.div>
      )}

      {/* Toast */}
      {toast && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="fixed top-20 left-1/2 -translate-x-1/2 z-40 px-4 py-2.5 rounded-xl glass-strong border border-gold-dim text-xs text-text-hi"
        >
          {toast}
        </motion.div>
      )}
    </div>
  );
};
